import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ViewTimelineOutlinedIcon from "@mui/icons-material/ViewTimelineOutlined";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchOssOrders } from "../../api/ossApi";
import { mockOrders } from "../../constants/mockData";
import { useOssAsyncData } from "../../hooks/useOssAsyncData";
import { OssPanel } from "../common/OssPanel";
import { OssDashboardConnectedCalendar } from "./OssDashboardConnectedCalendar";
import { OssDashboardGanttView } from "./OssDashboardGanttView";
import { normalizeTimelineRows } from "../../utils/timelineUtils";

const viewOptions = [
  { key: "gantt", label: "간트차트", icon: <ViewTimelineOutlinedIcon fontSize="small" /> },
  { key: "calendar", label: "달력", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
];

export const OssDashboardTimelinePanel = ({ onOpenOrder }) => {
  const { group_code, project_code } = useParams();
  const [view, setView] = useState("gantt");
  const { data, loading, error, load } = useOssAsyncData({
    fetcher: () =>
      fetchOssOrders(group_code, project_code, {
        page: 1,
        pageSize: 100,
        sortBy: "requiredStartDate",
        sortDir: "ASC",
      }),
    fallbackData: { items: mockOrders, pagination: { total: mockOrders.length } },
    errorMessage: "일정 데이터를 불러오지 못했습니다.",
    deps: [group_code, project_code],
  });

  const rows = useMemo(
    () => normalizeTimelineRows(data?.items?.length ? data.items : mockOrders),
    [data]
  );

  return (
    <OssPanel
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        maxHeight: "100%",
        boxSizing: "border-box",
        p: 1.75,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={1.2}
        sx={{ mb: 1.35, flexShrink: 0 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 900, color: "#111827" }}>
            착수-납기 일정 보기
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "#64748b" }}>
            품목별 착수 필요일부터 요청 납기까지의 기간을 연결해서 보여줍니다.
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.6} alignItems="center">
          {viewOptions.map((option) => (
            <Button
              key={option.key}
              size="small"
              variant={view === option.key ? "contained" : "outlined"}
              startIcon={option.icon}
              onClick={() => setView(option.key)}
              sx={{ minWidth: 106 }}
            >
              {option.label}
            </Button>
          ))}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" action={<Button color="inherit" onClick={load}>재시도</Button>} sx={{ mb: 1, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, height: "100%", maxHeight: "100%", display: "flex", overflow: "hidden" }}>
        {view === "gantt" ? (
          <OssDashboardGanttView rows={rows} loading={loading} onOpenOrder={onOpenOrder} />
        ) : (
          <OssDashboardConnectedCalendar rows={rows} onOpenOrder={onOpenOrder} />
        )}
      </Box>
    </OssPanel>
  );
};
